import { useQueryParams } from '@calendly/hooks'
import { useSelector } from 'react-redux'

import {
  useFetchAvailabilityBusyReportQuery,
  useFetchCancelledEventsReportQuery,
  useFetchMeetingsReportQuery,
  useFetchMeetingsReportQueryStateResult,
  useFetchMeetingsReportsQuery,
  useFetchPersonsReportQuery,
  useFetchRescheduledEventsReportQuery,
  useFetchUsersCalendarIntegrationsReportQuery,
  useFetchUsersWebConferencingIntegrationsReportQuery,
  useFetchRoutingFormReportQuery
} from 'hooks/reporting/api'
import { currentOrganizationSelector } from 'selectors/current_organization'

import { VALID_ROUTING_RANGES, ROUTING_DATE_RANGE_PERIODS } from './constants'
import { paramsSliceSelector } from './selectors'

const getUri = ({ uri }) => uri

const getSelectedUris = (items, uuids) => (
  uuids ? items.filter(({ uuid }) => uuids.includes(uuid)) : items
).map(getUri)

export const useFetchMeetingsReports = ({ status, start, end, uuids = null, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { users, teams, groups, eventTypes } = useSelector(paramsSliceSelector)
  const { service } = useQueryParams()

  return useFetchMeetingsReportsQuery({
    uuids,
    organization,
    status,
    start,
    end,
    ...(users?.length > 0 ? { users: getSelectedUris(users, uuids) } : {}),
    ...(teams?.length > 0 ? { teams: getSelectedUris(teams, uuids) } : {}),
    ...(groups?.length > 0 ? { groups: getSelectedUris(groups, uuids) } : {}),
    ...(eventTypes?.length > 0 ? { eventTypes: getSelectedUris(eventTypes, uuids) } : {}),
    ...(service ? { service } : {}),
    ...params
  }, {
    skip: !start || !end || start === end
  })
}

export const useFetchMeetingsReport = ({ status, start, end, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { users, teams, groups, eventTypes } = useSelector(paramsSliceSelector)
  const { service } = useQueryParams()

  return useFetchMeetingsReportQuery({
    organization,
    status,
    start,
    end,
    ...(users?.length > 0 ? { users: users.map(getUri) } : {}),
    ...(teams?.length > 0 ? { teams: teams.map(getUri) } : {}),
    ...(groups?.length > 0 ? { groups: groups.map(getUri) } : {}),
    ...(eventTypes?.length > 0 ? { eventTypes: eventTypes.map(getUri) } : {}),
    ...(service ? { service } : {}),
    ...params
  }, {
    skip: !start || !end || start === end
  })
}

export const useFetchMeetingsReportResults = ({ status, start, end, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { users, teams, groups, eventTypes } = useSelector(paramsSliceSelector)
  const { service } = useQueryParams()

  return useFetchMeetingsReportQueryStateResult({
    organization,
    status,
    start,
    end,
    ...(users?.length > 0 ? { users: users.map(getUri) } : {}),
    ...(teams?.length > 0 ? { teams: teams.map(getUri) } : {}),
    ...(groups?.length > 0 ? { groups: groups.map(getUri) } : {}),
    ...(eventTypes?.length > 0 ? { eventTypes: eventTypes.map(getUri) } : {}),
    ...(service ? { service } : {}),
    ...params
  }, {
    skip: !start || !end || start === end
  })
}

export const useFetchPersonsReport = ({ type, start, end, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { users, teams, groups, eventTypes } = useSelector(paramsSliceSelector)
  const { service } = useQueryParams()

  return useFetchPersonsReportQuery({
    organization,
    type,
    start,
    end,
    ...(users?.length > 0 ? { users: users.map(getUri) } : {}),
    ...(teams?.length > 0 ? { teams: teams.map(getUri) } : {}),
    ...(groups?.length > 0 ? { groups: groups.map(getUri) } : {}),
    ...(eventTypes?.length > 0 ? { eventTypes: eventTypes.map(getUri) } : {}),
    ...(service ? { service } : {}),
    ...params
  }, {
    skip: !start || !end
  })
}

export const useFetchCancelledMeetingsReport = ({ start, end, uuids = null, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { users, teams, groups, eventTypes } = useSelector(paramsSliceSelector)
  const { service } = useQueryParams()

  return useFetchCancelledEventsReportQuery({
    organization,
    start,
    end,
    ...(users?.length > 0 ? { users: getSelectedUris(users, uuids) } : {}),
    ...(teams?.length > 0 ? { teams: getSelectedUris(teams, uuids) } : {}),
    ...(groups?.length > 0 ? { groups: getSelectedUris(groups, uuids) } : {}),
    ...(eventTypes?.length > 0 ? { eventTypes: getSelectedUris(eventTypes, uuids) } : {}),
    ...(service ? { service } : {}),
    ...params
  }, {
    skip: !start || !end || start === end
  })
}

export const useFetchRescheduledMeetingsReport = ({ start, end, uuids = null, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { users, teams, groups, eventTypes } = useSelector(paramsSliceSelector)
  const { service } = useQueryParams()

  return useFetchRescheduledEventsReportQuery({
    organization,
    start,
    end,
    ...(users?.length > 0 ? { users: getSelectedUris(users, uuids) } : {}),
    ...(teams?.length > 0 ? { teams: getSelectedUris(teams, uuids) } : {}),
    ...(groups?.length > 0 ? { groups: getSelectedUris(groups, uuids) } : {}),
    ...(eventTypes?.length > 0 ? { eventTypes: getSelectedUris(eventTypes, uuids) } : {}),
    ...(service ? { service } : {}),
    ...params
  }, {
    skip: !start || !end || start === end
  })
}

export const useFetchAvailabilityBusyReport = ({ start, end, uuids = null, users = null, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { service } = useQueryParams()

  return useFetchAvailabilityBusyReportQuery({
    organization,
    start,
    end,
    uuids,
    ...(users?.length > 0 ? { users } : {}),
    ...(service ? { service } : {}),
    ...params
  }, {
    skip: !start || !end || start === end
  })
}

export const useFetchUsersCalendarIntegrationsReport = ({ skip, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { service } = useQueryParams()

  return useFetchUsersCalendarIntegrationsReportQuery({
    organization,
    ...(service ? { service } : {}),
    ...params
  }, { skip })
}

export const useFetchUsersVideoConferencingIntegrationsReport = ({ skip, ...params }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const { service } = useQueryParams()

  return useFetchUsersWebConferencingIntegrationsReportQuery({
    organization,
    ...(service ? { service } : {}),
    ...params
  }, { skip })
}

export const useFetchRoutingFormReport = ({ formId, start, end, range }) => {
  const { uri: organization } = useSelector(currentOrganizationSelector)
  const validRange = VALID_ROUTING_RANGES.includes(range)
    ? range
    : ROUTING_DATE_RANGE_PERIODS.ALL_TIME
  const { service } = useQueryParams()

  return useFetchRoutingFormReportQuery({
    organization,
    formId,
    start,
    end,
    range: validRange,
    ...(service ? { service } : {}),
  })
}
